import React, { useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchHealthData } from "../../store/UserSlice";
import { RootState } from "../../store/store";
import { fetchProgramsAll } from "../../store/ProgramSlice";
import Testimonials from "../../components/Testimonial";
import { HealthCondition } from "../../interfaces/HealthCondition";
import { AllPrograms } from "../../interfaces/Program";
import { programsDetailsConfig } from "../../SliderProperties/ProgramConfig";
import { healthConfig } from "../../SliderProperties/HealthConfig";
import { Link } from "react-router-dom";
import ProgramCard from "../../components/ProgramCard";
import NewsletterSection from "../../components/NewsletterSection";
import { useTranslation } from "react-i18next";

export default function Home() {
  const { t } = useTranslation();

  const slider = useRef<Slider | null>(null);
  const programsSlider = useRef<Slider | null>(null);

  const previous = () => {
    (slider.current as any)?.slickPrev();
  };
  const next = () => {
    (slider.current as any)?.slickNext();
  };

  const previousPrograms = () => {
    (programsSlider.current as any)?.slickPrev();
  };
  const nextPrograms = () => {
    (programsSlider.current as any)?.slickNext();
  };

  const dispatch = useDispatch<any>();
  const healthConditions = useSelector((state: RootState) => {
    const d = (state as any).user?.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray((d as any).healthConditions)) return (d as any).healthConditions;
    return [];
  }) as HealthCondition[];

  const programsAll = useSelector((state: RootState) => {
    return (state as any).programs?.data || ([] as AllPrograms[]);
  });

  useEffect(() => {
    // Dispatch the fetchHealthData action to fetch health data
    dispatch(fetchHealthData());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProgramsAll({ search: "" }));
  }, [dispatch]);

  localStorage.removeItem("SetSlug");

  return (
    <>
      <div className="relative">
        <div className="bg-[#050333] bg-center text-center text-white h-auto md:h-[calc(100vh-25px)]">
          <video
            autoPlay
            loop
            muted
            className="h-auto pt-[5.5rem] md:pt-0 md:h-[calc(100vh-25px)] w-full object-cover"
          >
            <source
              src="https://seva-media.s3.eu-central-1.amazonaws.com/Y2meta.app-DEPART+SANTE-(1080p).mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A2928] h-60"></div>
        </div>
      </div>

      <div
        className="bg-cover bg-center"
        style={{ backgroundImage: `url("./images/bg1.png")` }}
      >
        <div className="container mx-auto border-b border-[#595959] py-4 flex flex-col items-center justify-center">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <img
                src="./images/rme.png"
                alt="Logo RME"
                className="p-4 md:p-6 max-w-fit m-auto transition-transform duration-300 hover:scale-95"
              />
            </div>
            <div className="text-center">
              <img
                src="./images/asca.png"
                alt="Logo ASCA"
                className="p-4 md:p-6 max-w-fit transition-transform duration-300 hover:scale-95"
              />
            </div>
          </div>
          <div className="text-center max-w-3xl px-4 space-y-1 pb-3">
            <p className="text-xs sm:text-sm text-white/90 font-medium tracking-wide">
              {t("home.validOnlyIndividual")}
            </p>
            <p className="text-[11px] sm:text-xs text-[#9E9FBE] leading-relaxed tracking-wide">
              <span className="font-semibold text-white/80">
                {t("home.disclaimerLabel")}{" "}
              </span>
              {t("home.disclaimerText")}
            </p>
          </div>
        </div>

        <div className="container relative mx-auto my-10 items-center justify-center">
          <h4 className="text-center text-xl text-[#9E9FBE] pt-8">
            {t("home.servicesSubtitle")}
          </h4>
          <h2 className="text-center font-semibold text-2xl md:text-4xl mt-2 p-4 capitalize text-white tracking-wide mb-6">
            {t("home.servicesTitle")}
          </h2>

          <Slider
            ref={(c) => (slider.current = c || null)}
            {...healthConfig}
            className="px-6 py-12 pr-8"
          >
            {healthConditions.map((healthCondition: HealthCondition) => (
              <div
                key={healthCondition.id}
                className="group cursor-pointer text-center relative flex justify-center mb-12 select-none"
              >
                <Link
                  to="/programs"
                  state={{ id: healthCondition.id }}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <div className="absolute duration-1000 group-hover:bg-[#1D1A51] w-[80%] h-[80%] z-[-9] top-[10%] left-[10%] rounded-md"></div>
                  <div className="group-hover:bg-[#252264] w-[70%] duration-1000 h-[80%] flex flex-col py-20 justify-center items-center m-auto rounded-md">
                    <img
                      src={healthCondition.icon}
                      alt={healthCondition.name || "health condition"}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      className="px-4 h-28 object-scale-down w-full pointer-events-none select-none"
                    />
                    <h3 className="mt-4 text-white text-xl capitalize">
                      {healthCondition.name}
                    </h3>
                  </div>
                </Link>
              </div>
            ))}
          </Slider>

          <button
            onClick={previous}
            aria-label="Previous Health Condition"
            className="absolute top-[60%] left-0 sm:left-1 md:left-2 -translate-y-1/2 transition-transform duration-300 hover:scale-110 z-10 p-1"
          >
            <svg
              width="57"
              height="57"
              viewBox="0 0 57 57"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12"
            >
              <g filter="url(#filter0_d_17_75)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M28.5 2C16.0751 2 6 12.0727 6 24.5C6 36.9249 16.0751 47 28.5 47C40.9249 47 51 36.9249 51 24.5C51 12.0727 40.9249 2 28.5 2Z"
                  stroke="#7C7D9B"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <g filter="url(#filter1_d_17_75)">
                  <path
                    d="M32.0078 16.0565L23.5284 24.4995L32.0078 32.9425"
                    stroke="#7C7D9B"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    shapeRendering="crispEdges"
                  />
                </g>
              </g>
              <defs>
                <filter
                  id="filter0_d_17_75"
                  x="0.75"
                  y="0.75"
                  width="55.5"
                  height="55.5"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_17_75"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_17_75"
                    result="shape"
                  />
                </filter>
                <filter
                  id="filter1_d_17_75"
                  x="18.2784"
                  y="14.8065"
                  width="18.9795"
                  height="27.386"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_17_75"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_17_75"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </button>

          <button
            onClick={next}
            aria-label="Next Health Condition"
            className="absolute top-[60%] right-0 sm:right-1 md:right-2 -translate-y-1/2 transition-transform duration-300 hover:scale-110 z-10 p-1"
          >
            <svg
              width="57"
              height="57"
              viewBox="0 0 57 57"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-[60%] md:w-full"
            >
              <g filter="url(#filter0_d_17_72)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M28.5 47C40.9249 47 51 36.9273 51 24.5C51 12.0751 40.9249 2 28.5 2C16.0751 2 6 12.0751 6 24.5C6 36.9273 16.0751 47 28.5 47Z"
                  stroke="#7C7D9B"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <g filter="url(#filter1_d_17_72)">
                  <path
                    d="M24.9922 32.9435L33.4716 24.5005L24.9922 16.0575"
                    stroke="#7C7D9B"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    shapeRendering="crispEdges"
                  />
                </g>
              </g>
              <defs>
                <filter
                  id="filter0_d_17_72"
                  x="0.75"
                  y="0.75"
                  width="55.5"
                  height="55.5"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_17_72"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_17_72"
                    result="shape"
                  />
                </filter>
                <filter
                  id="filter1_d_17_72"
                  x="19.7422"
                  y="14.8075"
                  width="18.9795"
                  height="27.386"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_17_72"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_17_72"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </button>
        </div>
      </div>

      <div className="container mx-auto my-10 items-center justify-center text-center">
        <h4 className="text-center text-xl text-[#037897] pt-8">
          {t("home.programsSubtitle")}
        </h4>
        <h2 className="text-center font-semibold text-2xl md:text-4xl mt-2 p-4 capitalize text-[#020220] tracking-wide mb-4">
          {t("home.programsTitle")}
        </h2>
        <Link
          to="/programs"
          className="uppercase bg-[#037897] hover:bg-[#02627c] text-white py-3 my-4 font-bold rounded-md px-8 mb-8 inline-block transition-colors"
        >
          {t("home.allProgramsBtn")}
        </Link>

        {programsAll.length > 0 && (
          <div
            className="relative my-6 px-2 sm:px-6 md:px-10"
            aria-label="Carousel"
            role="region"
          >
            <Slider
              ref={(c) => (programsSlider.current = c || null)}
              {...programsDetailsConfig}
            >
              {programsAll.map((programs: AllPrograms) => (
                <div
                  key={programs.id}
                  className="p-3 h-full outline-none text-left flex flex-col"
                >
                  <ProgramCard program={programs} />
                </div>
              ))}
            </Slider>

            <button
              onClick={previousPrograms}
              aria-label="Previous Program"
              className="absolute top-1/2 left-0 sm:left-1 md:left-2 rotate-180 -translate-y-1/2 transition-transform duration-300 hover:scale-110 focus:outline-none z-10 p-1"
            >
              <svg
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-white/90 rounded-full shadow-md hover:bg-white"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M27 51.8164C40.8054 51.8164 52 40.6245 52 26.8164C52 13.011 40.8054 1.81641 27 1.81641C13.1946 1.81641 2 13.011 2 26.8164C2 40.6245 13.1946 51.8164 27 51.8164Z"
                  stroke="#037897"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M23.1017 36.1982L32.5234 26.8172L23.1017 17.4361"
                  stroke="#037897"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={nextPrograms}
              aria-label="Next Program"
              className="absolute top-1/2 right-0 sm:right-1 md:right-2 -translate-y-1/2 transition-transform duration-300 hover:scale-110 focus:outline-none z-10 p-1"
            >
              <svg
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-white/90 rounded-full shadow-md hover:bg-white"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M27 51.8164C40.8054 51.8164 52 40.6245 52 26.8164C52 13.011 40.8054 1.81641 27 1.81641C13.1946 1.81641 2 13.011 2 26.8164C2 40.6245 13.1946 51.8164 27 51.8164Z"
                  stroke="#037897"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M23.1017 36.1982L32.5234 26.8172L23.1017 17.4361"
                  stroke="#037897"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Newsletter Section (Placed right before "Qui sommes-nous" as requested by Esther) */}
      <NewsletterSection className="my-14" />

      <div
        className="container flex flex-col md:flex-row mx-auto my-12 px-4 sm:px-6 lg:px-8 items-start justify-between gap-8 lg:gap-12"
        id="about"
      >
        <div className="w-full md:w-5/12 lg:w-1/2 md:sticky md:top-24 self-start space-y-6">
          <div className="overflow-hidden rounded-2xl shadow-md border border-gray-100 bg-white">
            <img
              src="./images/about.png"
              alt="About SEVA DEPART SANTE"
              className="w-full h-auto object-cover rounded-2xl"
            />
          </div>

          {/* Outro Mission Card & CTA (Visible on Desktop / Two-column Layout) */}
          <div className="hidden md:block bg-[#F0F9FB] border border-[#037897]/20 rounded-2xl p-5 sm:p-6 shadow-xs">
            <h3 className="font-bold text-lg sm:text-xl text-[#041B44] mb-2">
              {t("home.aboutOutroTitle")}
            </h3>
            <p className="text-sm sm:text-base text-[#546E7A] mb-3 leading-relaxed">
              {t("home.aboutOutroText1")}
            </p>
            <p className="text-sm sm:text-base font-semibold text-[#037897] mb-5 leading-relaxed">
              {t("home.aboutOutroText2")}
            </p>
            <Link
              to="/contact"
              className="uppercase bg-[#037897] hover:bg-[#02627c] text-white py-3.5 leading-3 font-bold rounded-md px-8 inline-flex items-center transition-colors shadow-xs"
            >
              {t("home.contactBtn")}
              <svg
                className="inline pl-2"
                width="24"
                height="15"
                viewBox="0 0 24 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 7.5H2"
                  stroke="white"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.1914 2L22.715 7.49906L17.1914 13"
                  stroke="white"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
        <div className="w-full md:w-7/12 lg:w-1/2">
          <h4 className="text-lg sm:text-xl font-bold text-[#037897] mb-1">
            {t("home.aboutSubtitle")}
          </h4>
          <h2 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-[#020220] tracking-tight mt-1 mb-5">
            {t("home.aboutTitle")}
          </h2>
          <div className="mb-5 text-[#546E7A] whitespace-pre-line leading-relaxed text-sm sm:text-base">
            {t("home.aboutIntro")}
          </div>

          <div className="space-y-3">
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.detox.label")} :</b>{" "}
              {t("home.departSante.detox.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.water.label")} :</b>{" "}
              {t("home.departSante.water.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.posture.label")} :</b>{" "}
              {t("home.departSante.posture.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.friendship.label")} :</b>{" "}
              {t("home.departSante.friendship.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.rest.label")} :</b>{" "}
              {t("home.departSante.rest.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.work.label")} :</b>{" "}
              {t("home.departSante.work.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.sun.label")} :</b>{" "}
              {t("home.departSante.sun.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.air.label")} :</b>{" "}
              {t("home.departSante.air.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.nutrition.label")} :</b>{" "}
              {t("home.departSante.nutrition.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.temperance.label")} :</b>{" "}
              {t("home.departSante.temperance.text")}
            </p>
            <p className="text-[#546E7A] leading-relaxed">
              <b className="text-[#041B44]">{t("home.departSante.exercise.label")} :</b>{" "}
              {t("home.departSante.exercise.text")}
            </p>
          </div>

          {/* Outro Mission Card & CTA (Visible on Mobile / Single-column Layout) */}
          <div className="block md:hidden bg-[#F0F9FB] border border-[#037897]/20 rounded-2xl p-5 mt-6 shadow-xs">
            <h3 className="font-bold text-lg text-[#041B44] mb-2">
              {t("home.aboutOutroTitle")}
            </h3>
            <p className="text-sm text-[#546E7A] mb-3 leading-relaxed">
              {t("home.aboutOutroText1")}
            </p>
            <p className="text-sm font-semibold text-[#037897] mb-5 leading-relaxed">
              {t("home.aboutOutroText2")}
            </p>
            <Link
              to="/contact"
              className="uppercase bg-[#037897] hover:bg-[#02627c] text-white py-3.5 leading-3 font-bold rounded-md px-8 inline-flex items-center transition-colors shadow-xs"
            >
              {t("home.contactBtn")}
              <svg
                className="inline pl-2"
                width="24"
                height="15"
                viewBox="0 0 24 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 7.5H2"
                  stroke="white"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.1914 2L22.715 7.49906L17.1914 13"
                  stroke="white"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      <Testimonials />
    </>
  );
}
